import { action, observable, reaction } from 'mobx';
import * as IoC from 'typescript-ioc';
import { IAlertsService } from '../../../../../infrastructure/alerts';
import React from 'react';
import { IKnovioPreviewLinkDAO, IKnovioConnectionPreviewLinkDTO, IKnovioArtifactPreviewLinkDTO } from '../../../../../api/knovio-preview-link.dao';
import { MeetingApiParams } from '../connections-list/_connection-form/connection-form.viewmodel';
import { IGlobalStorage } from '../../../../../infrastructure/global-storage';
import TrackingServiceProvider from '../../../../../tracking-service/tracking-service-provider';
import { TRACKING_EVENTS } from '../../../../../tracking-service/tracking-values';
import { AlertErrorMessage } from '../../../../global/modal/alerts-host/types/alert/alert-error-message';
import { IntlShape } from 'react-intl';
import { IReactIntlProvider } from '../../../../../infrastructure/react-intl-provider';
import { KnovioLabel } from '../../../../../api/connections.dao';

enum KnovioPreviewSourceType {
  Connection = 'Connection',
  Artifact = 'Artifact',
}

interface IKnovioPreviewLinkMeetingParams extends MeetingApiParams {
    meetingConnectionDetailsId?: number | string;
    assetMetadataId?: number | string;
}

class KnovioPreviewLinkPopoverVM {
    @observable
    private isOpen: boolean = false;
    @observable
    private isLoading: boolean = true;
    @observable
    private previewLink: string | null = null;

    private controller: AbortController | null = null;
    private isError: boolean = false;

    private knovioConnectionInfo: IKnovioPreviewLinkMeetingParams | null = null;

    constructor(
      private readonly knovioPreviewSourceType: KnovioPreviewSourceType,
      @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
      @IoC.Inject private readonly intlProvider: IReactIntlProvider = IoC.Container.get(IReactIntlProvider),
      @IoC.Inject private readonly knovioPreviewLinkDAO: IKnovioPreviewLinkDAO = IoC.Container.get(IKnovioPreviewLinkDAO),
      @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
      @IoC.Inject private readonly globalStorage: IGlobalStorage = IoC.Container.get(IGlobalStorage)
      ) {

        reaction(() => this.isOpen, isOpen => {
          if (isOpen) {
            this.sendTrackingEvent();
          } else {
            this.onCloseCallbackBuilder();
          };
        });
      }
    public get intl(): IntlShape { return this.intlProvider.getIntl(); }

    public get knovioPreviewLink(): string {
      return this.previewLink;
    }

    public get isPopoverOpened(): boolean {
        return this.isOpen;
    }

    public get isLinkLoading(): boolean {
      return this.isLoading;
    }

    public get isDisabled(): boolean {
      return !this.knovioPreviewLink && this.isLinkLoading;
    }

    // Check if all required fields passed
    public get isInstanceReady(): string | number {
      return (
        this.knovioConnectionInfo.accountId &&
        this.knovioConnectionInfo.eventId &&
        (this.knovioConnectionInfo.meetingConnectionDetailsId || this.knovioConnectionInfo.assetMetadataId));
    }

    @action
    public closePopover = (): void => {
      this.isOpen = false;
    };

    @action
    public openPopover = (): void => {
      this.isOpen = true;
    };

    @action
    public fetchPreviewLink = async (): Promise<void> => {
      this.knovioPreviewSourceType === KnovioPreviewSourceType.Connection
        ? this.fetchKnovioConnectionPreviewLink()
        : this.fetchKnovioArtifactPreviewLink();
    };

    public setConnectionInfo = (info: IKnovioPreviewLinkMeetingParams): void => {
      this.knovioConnectionInfo = info;
    };

    public openKnovioPreviewLink = (): void => {
      window.open(this.knovioPreviewLink, '_blank');
    };

    private fetchKnovioConnectionPreviewLink = async (): Promise<void> => {
      try {
        if (!this.isInstanceReady) {
          throw new Error('Missed fields');
        }

        this.startLoading();
        this.initController();

        const {
          presentationLink
        } = await this.knovioPreviewLinkDAO.generateKnovioConnectionPreviewLink(this.getKnovioConnectionParams(), this.controller.signal);

        this.setPreviewLink(presentationLink);
        this.stopLoading();
      } catch (e) {
        this.handleException(e);
      }
    };

    private fetchKnovioArtifactPreviewLink = async (): Promise<void> => {
      try {
        if (!this.isInstanceReady) {
          throw new Error('Missed fields');
        }

        this.startLoading();
        this.initController();

        const { presentationLink } = await this.knovioPreviewLinkDAO.generateKnovioArtifactPreviewLink(this.getKnovioArtifactParams(), this.controller.signal);

        this.setPreviewLink(presentationLink);
        this.stopLoading();
      } catch (e) {
        this.handleException(e);
      }
    };

    private getKnovioConnectionParams = (): IKnovioConnectionPreviewLinkDTO => {
      return {
        eventId: this.knovioConnectionInfo.eventId,
        accountId: this.knovioConnectionInfo.accountId,
        meetingConnectionId: this.knovioConnectionInfo.meetingConnectionDetailsId,
        expirationDate: this.getExpirationDate(),
      };
    };

    private getKnovioArtifactParams = (): IKnovioArtifactPreviewLinkDTO => {
      return {
        eventId: this.knovioConnectionInfo.eventId,
        accountId: this.knovioConnectionInfo.accountId,
        meetingId: this.knovioConnectionInfo.meetingId,
        assetMetadataId: this.knovioConnectionInfo.assetMetadataId,
        expirationDate: this.getExpirationDate(),
      };
    };

    private getExpirationDate = (): Date => {
      const hoursInDay = 24;
      const currentDate = new Date();
      currentDate.setHours(currentDate.getHours() + hoursInDay);
      return currentDate;
    };

    @action
    private setPreviewLink = (previewLink: string | null): void => {
      this.previewLink = previewLink;
    };

    @action
    private startLoading = (): void => {
      this.isLoading = true;
    };

    @action
    private stopLoading = (): void => {
      this.isLoading = false;
    };

    private initController = (): void => {
      this.controller = new AbortController();
    };

    private deleteController = (): void => {
      this.controller = null;
    };

    private cancellRequest = (): void => {
      this.controller.abort();
    };

    private setIsError = (toggle = true): void => {
      this.isError = toggle;
    };

    @action
    private onCloseCallbackBuilder = (): void => {
      if (!this.isError && this.isLoading) {
        // Cancel request if modal force closed before API request is resolved
        this.cancellRequest();
      }

      this.deleteController();
      this.stopLoading();
      this.setIsError(false);
      this.setPreviewLink(null);
    };

    private handleException = (e: Error): void => {
      if (e.name === 'AbortError') {
        // Ignore this error type
        return;
      }

      this.setIsError();
      this.alertsService.alert({
        message: (
          <AlertErrorMessage title='Error' message={`There is no ${KnovioLabel.OEStream} with that Embed Code ID in your Account.  Please check the details in ${KnovioLabel.OEStream} and try again.`} />
        ),
        renderCrossIcon: false,
        okButtonProps: {
            text: 'Ok',
            handler: this.closePopover.bind(this),
        },
      });
    };

    private sendTrackingEvent = (): void => {
      this.trackingService.get().track(TRACKING_EVENTS.KNOVIO_PREVIEW_CONNECTION, {
        ['Account ID']: this.knovioConnectionInfo.accountId,
        ['Account Name']: this.globalStorage.getEvent()?.accountName,
        ['Event ID']: this.knovioConnectionInfo.eventId,
        ['Event Name']: this.globalStorage.getEvent().name,
        ...(this.knovioConnectionInfo.roomId && {
          ['Room ID']: this.knovioConnectionInfo.roomId
        }),
        ...(this.knovioConnectionInfo.meetingId && {
          ['Meeting ID']: this.knovioConnectionInfo.meetingId
        }),
        ['Connection ID']: this.knovioConnectionInfo.meetingConnectionDetailsId
      });
    };
}

export { KnovioPreviewSourceType };
export default KnovioPreviewLinkPopoverVM;
